import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { 
  HardDrive,
  DollarSign,
  TrendingUp,
  Server,
  Zap,
  Cloud,
  Package,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const COLORS = [
  '#10b981', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
];

/**
 * StorageAnalytics - Shows storage usage, costs, and growth trends
 * 
 * Features:
 * - Total storage usage and trends
 * - Cost breakdown and projections
 * - Compression savings
 * - Storage efficiency metrics
 * - Regional distribution
 */
const StorageAnalytics = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-4 text-gray-500">
        No storage analytics data available
      </div>
    );
  }

  const chartConfig = {
    storage: {
      label: "Storage (GB)",
      color: "#3b82f6",
    },
    cost: {
      label: "Cost ($)",
      color: "#10b981",
    },
  };

  const costBreakdownData = [
    { name: 'Storage', value: data.costBreakdown.storage, color: '#3b82f6' },
    { name: 'Bandwidth', value: data.costBreakdown.bandwidth, color: '#10b981' },
    { name: 'Requests', value: data.costBreakdown.requests, color: '#f59e0b' },
  ];

  const totalCost = Object.values(data.costBreakdown).reduce((sum, cost) => sum + cost, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatStorage = (gb) => {
    if (gb >= 1000) {
      return `${(gb / 1000).toFixed(1)} TB`;
    }
    return `${gb.toFixed(1)} GB`;
  };

  return (
    <div className="space-y-6">
      {/* Storage Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <HardDrive className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-500">Total Storage</span>
            </div>
            <div className="text-2xl font-bold mt-3">{formatStorage(data.totalUsage)}</div>
            <div className="flex items-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <p className="text-xs text-green-600">+2.3 GB this month</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-500">Monthly Cost</span>
            </div>
            <div className="text-2xl font-bold mt-3">{formatCurrency(totalCost)}</div>
            <div className="flex items-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-red-600" />
              <p className="text-xs text-red-600">+$8.50 from last month</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-500">Compression Savings</span>
            </div>
            <div className="text-2xl font-bold mt-3">{data.compressionSavings.toFixed(1)}%</div>
            <p className="text-xs text-gray-500 mt-2">Space saved through optimization</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-500">Files Stored</span>
            </div>
            <div className="text-2xl font-bold mt-3">
              {Math.round(data.totalUsage * 45)}
            </div>
            <p className="text-xs text-gray-500 mt-2">Total files across all versions</p>
          </CardContent>
        </Card>
      </div>

      {/* Storage Growth Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Storage Growth & Cost Trends</span>
          </CardTitle>
          <CardDescription>
            Historical storage usage and associated costs over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.growthTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value + '-01');
                    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                  }}
                />
                <YAxis 
                  yAxisId="storage"
                  tick={{ fontSize: 12 }} 
                  tickFormatter={(value) => `${value}GB`}
                />
                <YAxis 
                  yAxisId="cost"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <ChartTooltip 
                  content={<ChartTooltipContent />}
                  labelFormatter={(value) => {
                    const date = new Date(value + '-01');
                    return date.toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    });
                  }}
                  formatter={(value, name) => [
                    name === 'storage' ? `${value} GB` : formatCurrency(value),
                    name === 'storage' ? 'Storage Used' : 'Monthly Cost'
                  ]}
                />
                <Area
                  yAxisId="storage"
                  type="monotone"
                  dataKey="storage"
                  stroke="var(--color-storage)"
                  fill="var(--color-storage)"
                  fillOpacity={0.6}
                />
                <Line
                  yAxisId="cost"
                  type="monotone"
                  dataKey="cost"
                  stroke="var(--color-cost)"
                  strokeWidth={2}
                  dot={{ fill: "var(--color-cost)", strokeWidth: 2, r: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5" />
              <span>Cost Breakdown</span>
            </CardTitle>
            <CardDescription>
              Monthly cloud storage costs by category
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costBreakdownData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                  >
                    {costBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value, name) => [formatCurrency(value), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            
            {/* Cost Legend */}
            <div className="space-y-3 mt-4">
              {costBreakdownData.map((item, index) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm font-medium">{item.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-bold">{formatCurrency(item.value)}</span>
                    <span className="text-xs text-gray-500">
                      ({Math.round((item.value / totalCost) * 100)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Storage Efficiency */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Zap className="h-5 w-5" />
              <span>Storage Efficiency</span>
            </CardTitle>
            <CardDescription>
              Optimization metrics and potential savings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Compression Ratio */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Package className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Compression Efficiency</span>
                </div>
                <Badge className="bg-green-100 text-green-800">
                  {data.compressionSavings.toFixed(1)}% saved
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-green-500 transition-all duration-300"
                  style={{ width: `${data.compressionSavings}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">
                Saved {formatStorage(data.totalUsage * (data.compressionSavings / 100))} through compression
              </p>
            </div>

            {/* Deduplication */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Server className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Deduplication Rate</span>
                </div>
                <Badge className="bg-blue-100 text-blue-800">
                  18.3% saved
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                  style={{ width: '18.3%' }}
                />
              </div>
              <p className="text-xs text-gray-500">
                Eliminated {formatStorage(data.totalUsage * 0.183)} of duplicate files
              </p>
            </div>

            {/* Cache Hit Rate */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Cloud className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium">CDN Cache Efficiency</span>
                </div>
                <Badge className="bg-purple-100 text-purple-800">
                  94.2% hit rate
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-purple-500 transition-all duration-300"
                  style={{ width: '94.2%' }}
                />
              </div>
              <p className="text-xs text-gray-500">
                Reduced bandwidth costs by {formatCurrency(data.costBreakdown.bandwidth * 0.6)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Storage Health & Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Server className="h-5 w-5" />
            <span>Storage Health & Recommendations</span>
          </CardTitle>
          <CardDescription>
            Current system status and optimization opportunities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Health Status */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">System Health</h4>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900">Storage Capacity</span>
                </div>
                <Badge className="bg-green-100 text-green-800">Healthy</Badge>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900">Redundancy</span>
                </div>
                <Badge className="bg-green-100 text-green-800">99.99%</Badge>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-900">Cost Growth</span>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800">Monitor</Badge>
              </div>
            </div>

            {/* Recommendations */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Optimization Recommendations</h4>
              
              <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Zap className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Implement Lifecycle Policies</p>
                    <p className="text-xs text-blue-700">
                      Archive versions older than 6 months to save ~{formatCurrency(totalCost * 0.25)}/month
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 border border-green-200 bg-green-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Package className="h-4 w-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Enhanced Compression</p>
                    <p className="text-xs text-green-700">
                      Switch to WebP format for images to save additional {formatStorage(data.totalUsage * 0.15)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 border border-purple-200 bg-purple-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Cloud className="h-4 w-4 text-purple-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-purple-900">CDN Optimization</p>
                    <p className="text-xs text-purple-700">
                      Enable edge caching for PDFs to reduce bandwidth by ~{formatCurrency(data.costBreakdown.bandwidth * 0.3)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Storage Metrics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <HardDrive className="h-6 w-6 text-blue-600" />
          </div>
          <div className="text-xl font-bold text-blue-600 mb-1">
            {(data.totalUsage / (Math.round(data.totalUsage * 45) / 1000)).toFixed(1)}
          </div>
          <p className="text-xs text-blue-700 font-medium">Average File Size</p>
          <div className="text-xs text-blue-600 mt-1">MB per file</div>
        </div>

        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <DollarSign className="h-6 w-6 text-green-600" />
          </div>
          <div className="text-xl font-bold text-green-600 mb-1">
            {(totalCost / data.totalUsage).toFixed(2)}
          </div>
          <p className="text-xs text-green-700 font-medium">Cost per GB</p>
          <div className="text-xs text-green-600 mt-1">Monthly average</div>
        </div>

        <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Zap className="h-6 w-6 text-purple-600" />
          </div>
          <div className="text-xl font-bold text-purple-600 mb-1">
            {formatCurrency(totalCost * (data.compressionSavings / 100))}
          </div>
          <p className="text-xs text-purple-700 font-medium">Monthly Savings</p>
          <div className="text-xs text-purple-600 mt-1">Through optimization</div>
        </div>

        <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="h-6 w-6 text-amber-600" />
          </div>
          <div className="text-xl font-bold text-amber-600 mb-1">
            {((data.growthTrends[data.growthTrends.length - 1]?.storage - data.growthTrends[0]?.storage) / data.growthTrends.length * 12).toFixed(1)}
          </div>
          <p className="text-xs text-amber-700 font-medium">Annual Growth</p>
          <div className="text-xs text-amber-600 mt-1">GB projected</div>
        </div>
      </div>
    </div>
  );
};

export default StorageAnalytics;